import Chart, { useChart } from 'src/components/chart';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { Box } from '@mui/system';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useRef } from 'react';
import { generateAnnotations } from 'src/utils/graph-demo-annotations';
import { formatNumber } from 'src/utils/format-number';
import { formatCurrency } from 'src/utils/format-currency';

type Props = {
  series: {
    name: string;
    data: number[];
  }[];
  labels: string[];
  height?: number;
  /** Override the per-series fill colors. Falls back to the legacy palette when omitted. */
  colors?: string[];
  /** Top radius applied to the last stacked segment of each column. */
  barBorderRadius?: number;
  /** Render the value of each segment on the bars. */
  showDataLabels?: boolean;
  /** Diameter (px) of the round legend markers. */
  legendMarkerSize?: number;
  /** Override the legend label text color. */
  legendLabelColor?: string;
  /** Hide the built-in ApexCharts legend (use when rendering a custom legend outside). */
  hideLegend?: boolean;
  /** Format values as currency (using the workspace currency) instead of plain numbers. */
  currency?: boolean;
};

export default function ChartColumnStacked({
  series,
  labels,
  height = 350,
  colors,
  barBorderRadius = 0,
  showDataLabels = false,
  legendMarkerSize = 16,
  legendLabelColor,
  hideLegend = false,
  currency = false,
}: Props) {
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const workspace = useCurrentWorkspace();
  const annotations = generateAnnotations(workspace, filterStore, labels);
  const chartRef = useRef(null);
  const lang = localStorage.getItem('i18nextLng') || 'en';
  const formatValue = (val: number) =>
    currency ? formatCurrency(val, workspace?.currency || 'USD', lang, true, 1) : formatNumber(val);
  
  const resolvedColors =
    colors ??
    (series?.length === 3
      ? [theme?.palette.success.light, theme.palette.tertiary.light, '#b7b7b7']
      : series?.length === 5
        ? [theme.palette.success.light, '#1e88e5', '#64b5f6', '#b7dcfb', '#b7b7b7']
        : ['#118d57', '#1e88e5', '#64b5f6', '#E1E1E1']);

  const chartOptions = useChart({
    chart: {
      stacked: true,
      animations: {
        enabled: true,
        speed: 500,
        animateGradually: {
          enabled: true,
          delay: 200,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 300,
        },
      },
    },
    series: series,
    stroke: {
      width: 0,
      show: false,
    },
    labels: labels || [],
    // Give the x-axis date labels a little breathing room (gap above + below)
    // so they don't sit cramped against the bars / card edge.
    xaxis: {
      labels: { offsetY: 4 },
    },
    grid: {
      padding: { bottom: 8 },
    },
    legend: {
      show: !hideLegend,
      itemMargin: {
        vertical: 8,
      },
      markers: {
        customHTML: function () {
          return `<div style="width: ${legendMarkerSize}px; height: ${legendMarkerSize}px; border-radius: 50%; background-color: currentcolor;"></div>`;
        },
      },
      position: 'top',
      offsetY: 0,
      ...(legendLabelColor ? { labels: { colors: legendLabelColor } } : {}),
    },
    dataLabels: showDataLabels
      ? {
          enabled: true,
          formatter: (val: number) => (val && val > 0 ? formatValue(val) : ''),
          style: {
            fontSize: '11px',
            fontWeight: 600,
            fontFamily: 'Public Sans, sans-serif',
            colors: ['#1c252e'],
          },
          dropShadow: { enabled: false },
          background: { enabled: false },
        }
      : { enabled: false },
    fill: {
      colors: resolvedColors,
    },
    colors: resolvedColors,
    plotOptions: {
      bar: {
        barHeight: '80%',
        columnWidth: '70%',
        borderRadius: barBorderRadius,
        distributed: false,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: function ({ series, dataPointIndex, w }) {
        if (!series || !w.globals.seriesNames || !w.globals.colors) {
          return '';
        }

        const date = w.globals.labels[dataPointIndex] || '';
        const collapsed = [
          ...(w.globals.collapsedSeriesIndices || []),
          ...(w.globals.ancillaryCollapsedSeriesIndices || []),
        ];

        const rows = series.map((s: any, i: any) => {
          if (collapsed.includes(i)) return '';
          const value = s[dataPointIndex];
          const formattedValue = value != null && !isNaN(value) ? formatValue(value) : '0';
          return `
            <div style="display:flex;align-items:center;gap:8px;padding-left:4px;">
              <span style="display:inline-block;width:2px;height:12px;border-radius:2px;background-color:${w.globals.colors[i]};flex-shrink:0;"></span>
              <div style="flex:1;display:flex;justify-content:space-between;align-items:center;font-family:Public Sans,sans-serif;font-size:13px;font-weight:500;color:#333c47;white-space:nowrap;gap:16px;">
                <span>${w.globals.seriesNames[i]}</span>
                <span>${formattedValue}</span>
              </div>
            </div>
          `;
        }).join('');

        return `
          <div style="background:#fcfcfd;border:1px solid rgba(42,50,59,0.2);border-radius:12px;box-shadow:1px 2px 5px rgba(0,0,0,0.06);overflow:hidden;min-width:240px;">
            <div style="padding:12px 12px 0 12px;">
              <p style="font-family:Public Sans,sans-serif;font-size:14px;font-weight:700;color:#333c47;white-space:nowrap;margin:0;">${date}</p>
            </div>
            <div style="padding:12px;display:flex;flex-direction:column;gap:6px;">
              ${rows}
            </div>
          </div>
        `;
      },
      y: {
        formatter: (val: number) => {
          if (val == null || isNaN(val)) {
            return '0';
          }
          return formatValue(val);
        },
      },
    },
    yaxis: [
      {
        labels: { 
          formatter(value) {
            if (value == null || isNaN(value)) {
              return '0';
            }
            return formatValue(value);
          }
        },
      },
    ],
    annotations: {
      xaxis: annotations,
    },
  });

  return (
    <Box sx={{
      'span .apexcharts-legend-marker': {
        borderRadius: 100,
      },
    }}>
      <Chart ref={chartRef} dir="ltr" type="bar" series={series} options={chartOptions} height={height} />
    </Box>
  );
}